import React, { useMemo } from 'react';
import type {
  Candidate,
  Experience,
  Taxonomy,
  TaxonomyFocusAreaEnum,
} from '@axiom/validation';
import {
  Form,
  Button,
  Gutter,
  Well,
  SectionHeader,
  Paragraph,
  Layout,
  Modal,
  ModalHeader,
  ModalSection,
  Checkbox,
  CondensedHeader,
  Grid,
  GridColumn,
  FormGroup,
  ModalFooter,
  Brief,
  CandidateTaxonomyUtil,
  TaxonomyUtil,
  useBreakpoint,
} from '@axiom/ui';

import { ExperienceEditUtil } from './experience-edit-util';
import {
  ExperienceEditFocusAreasSchema,
  FocusAreasFormSchemaNonAxiom,
} from './experience-edit-focus-areas-schema';

export type ExperienceEditFormFocusAreasSelectProps = {
  candidate: Candidate;
  experience: Experience;
  focusAreas: TaxonomyFocusAreaEnum[];
  name: string;
  onFocusAreasSelected: (
    focusAreas: TaxonomyFocusAreaEnum[],
    noFocusAreasApply?: boolean
  ) => Promise<void>;
  taxonomy: Taxonomy;
};

const noAxiomFocusAreasApplyCheckboxName = 'noAxiomFocusAreasApply';

export const ExperienceEditFormFocusAreasSelect = ({
  candidate,
  experience,
  focusAreas,
  name,
  onFocusAreasSelected,
  taxonomy,
}: ExperienceEditFormFocusAreasSelectProps) => {
  const { isTablet } = useBreakpoint();
  const focusAreasByPracticeArea = useMemo(
    () =>
      TaxonomyUtil.getFocusAreaEnumsByPracticeAreaEnumForCandidate(
        taxonomy,
        candidate
      ),
    [taxonomy, candidate]
  );

  const focusAreaSuggestions = useMemo(() => {
    const isAlp = !TaxonomyUtil.isLawyer(candidate.businessTitle);
    return CandidateTaxonomyUtil.getFocusAreasFromExperienceTaxonomy(
      experience.experienceTaxonomy
    ).filter(({ dsSuggestion, focusArea }) => {
      const tfa = taxonomy.focusAreas[focusArea];
      return dsSuggestion && (!isAlp || tfa?.alpEligible) && tfa?.selectable;
    });
  }, [experience, candidate]);

  const onModalCancel = async () => {
    await onFocusAreasSelected(focusAreas);
  };

  return (
    <Form
      name={name}
      initialValues={{
        focusAreas,
        ...(experience.isAxiom
          ? {}
          : {
              [noAxiomFocusAreasApplyCheckboxName]:
                focusAreas.length === 0 &&
                !!experience.noFocusAreasApplyMarkedAt,
            }),
      }}
      onSubmit={async changedData =>
        onFocusAreasSelected(
          changedData.focusAreas ?? focusAreas,
          'noAxiomFocusAreasApply' in changedData &&
            (changedData.noAxiomFocusAreasApply ?? false)
        )
      }
      schema={
        experience.isAxiom
          ? ExperienceEditFocusAreasSchema
          : FocusAreasFormSchemaNonAxiom
      }
    >
      {({ fireSubmit, values, setValues }) => {
        const clearNoFocusAreasApplyCheckbox = async () => {
          if (
            'noAxiomFocusAreasApply' in values &&
            values[noAxiomFocusAreasApplyCheckboxName]
          ) {
            await setValues({
              ...values,
              [noAxiomFocusAreasApplyCheckboxName]: false,
            });
          }
        };
        const focusAreaCheckboxesByPracticeArea = Object.keys(
          focusAreasByPracticeArea
        )
          .sort((a, b) => {
            const aName = taxonomy.practiceAreas[a]?.fullName ?? '';
            const bName = taxonomy.practiceAreas[b]?.fullName ?? '';
            return aName < bName ? -1 : 1;
          })
          .map(practiceArea => {
            return (
              <div
                key={practiceArea}
                data-test="EXPERIENCE_EDIT_FORM_SELECT_CHECKBOX_GROUP"
              >
                <CondensedHeader name="EXPERIENCE_EDIT_FORM_SELECT_CHECKBOX_GROUP_HEADER">
                  {taxonomy.practiceAreas[practiceArea]?.fullName}
                </CondensedHeader>
                <Gutter bottom="8px" />
                {focusAreasByPracticeArea[practiceArea]
                  ?.sort(
                    (
                      a: keyof typeof taxonomy.focusAreas,
                      b: keyof typeof taxonomy.focusAreas
                    ) => {
                      const aName = taxonomy.focusAreas[a]?.fullName ?? '';
                      const bName = taxonomy.focusAreas[b]?.fullName ?? '';
                      return aName < bName ? -1 : 1;
                    }
                  )
                  .map(focusArea => (
                    <div
                      key={focusArea}
                      data-test="EXPERIENCE_EDIT_FORM_SELECT_CHECKBOX_GROUP_CHECKBOX"
                    >
                      <Checkbox
                        displayValue={taxonomy.focusAreas[focusArea]?.fullName}
                        name="focusAreas"
                        option={focusArea}
                        onChange={clearNoFocusAreasApplyCheckbox}
                        id={`grid-focusAreas-${focusArea}`}
                      />
                      <Gutter bottom="8px" />
                    </div>
                  ))}
              </div>
            );
          });
        const focusAreasCheckboxGrid = isTablet ? (
          <Grid name="EDIT_EXPERIENCE_FORM_CHECKBOX_GRID_TABLET">
            <GridColumn width={6}>
              <Layout direction="vertical" verticalGutter="16px">
                {focusAreaCheckboxesByPracticeArea.slice(0, 5)}
              </Layout>
            </GridColumn>
            <GridColumn width={6}>
              <Layout direction="vertical" verticalGutter="16px">
                {focusAreaCheckboxesByPracticeArea.slice(5)}
              </Layout>
            </GridColumn>
          </Grid>
        ) : (
          <Grid name="EDIT_EXPERIENCE_FORM_CHECKBOX_GRID_DEFAULT">
            <GridColumn largeScreenWidth={4} mobileWidth={12}>
              <Layout direction="vertical" verticalGutter="16px">
                {focusAreaCheckboxesByPracticeArea.slice(0, 4)}
              </Layout>
            </GridColumn>
            <GridColumn largeScreenWidth={4} mobileWidth={12}>
              <Layout direction="vertical" verticalGutter="16px">
                {focusAreaCheckboxesByPracticeArea.slice(4, 8)}
              </Layout>
            </GridColumn>
            <GridColumn largeScreenWidth={4} mobileWidth={12}>
              <Layout direction="vertical" verticalGutter="16px">
                {focusAreaCheckboxesByPracticeArea.slice(8)}
              </Layout>
            </GridColumn>
          </Grid>
        );
        return (
          <Modal
            size="large"
            name="EXPERIENCE_EDIT_FORM_SELECT_FOCUS_AREAS_MODAL"
          >
            <ModalHeader
              name="EXPERIENCE_EDIT_FORM_SELECT_FOCUS_AREAS_MODAL_HEADER"
              onClose={async () => onModalCancel()}
            >
              Focus areas
            </ModalHeader>
            <ModalSection>
              <Well background="blue">
                <SectionHeader name="EXPERIENCE_EDIT_FORM_SELECT_FOCUS_AREAS_WELL_HEADER">
                  What focus areas did you work in during your engagement with{' '}
                  {ExperienceEditUtil.getExperienceClientDateRangeLabel(
                    experience
                  )}
                  ?
                </SectionHeader>
              </Well>
              <Gutter bottom="16px" />
              <FormGroup name="focusAreas">
                {focusAreaSuggestions.length > 0 && (
                  <Well name="EXPERIENCE_EDIT_FORM_FOCUS_AREA_SUGGESTION_WELL">
                    <Paragraph>
                      Here are suggested focus areas based on the work
                      description you provided.
                    </Paragraph>
                    <Gutter bottom="16px" />
                    <Layout horizontalGutter="8px" verticalGutter="8px" wrap>
                      {focusAreaSuggestions.map(({ focusArea }) => (
                        <Checkbox
                          key={focusArea}
                          displayValue={
                            taxonomy.focusAreas[focusArea]?.fullName
                          }
                          mode="pill"
                          name="focusAreas"
                          option={focusArea}
                          onChange={clearNoFocusAreasApplyCheckbox}
                          id={`suggestion-focusAreas-${focusArea}`}
                        />
                      ))}
                    </Layout>
                  </Well>
                )}
                <Gutter bottom="16px" />
                {focusAreaSuggestions.length > 0 ? (
                  <Brief
                    name="EXPERIENCE_EDIT_FORM_SELECT_FOCUS_AREA_BRIEF"
                    briefMaxHeight="0"
                    label="Focus Areas"
                    expanded={values.focusAreas?.some(
                      focusArea =>
                        !focusAreaSuggestions.some(
                          suggestion => suggestion.focusArea === focusArea
                        )
                    )}
                  >
                    {focusAreasCheckboxGrid}
                  </Brief>
                ) : (
                  focusAreasCheckboxGrid
                )}
                {!experience.isAxiom && (
                  <Gutter top="16px">
                    <Checkbox
                      displayValue="No Axiom Focus Areas apply to this work. Check this box if this experience is a non-legal experience or in a legal area not supported by Axiom."
                      name={noAxiomFocusAreasApplyCheckboxName}
                      onChange={async value => {
                        if (value) {
                          await setValues({
                            ...values,
                            focusAreas: [],
                          });
                        }
                      }}
                    />
                  </Gutter>
                )}
              </FormGroup>
            </ModalSection>
            <ModalFooter>
              <Button
                name="EXPERIENCE_EDIT_FORM_SELECT_CANCEL"
                onClick={async () => onModalCancel()}
                variation="outline"
              >
                Cancel
              </Button>
              <Button
                name="EXPERIENCE_EDIT_FORM_SELECT_SUBMIT"
                onClick={async () => fireSubmit()}
              >
                Add focus areas
              </Button>
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
